import routes from '../../../constants/routes';
import { IPassengerDetail, IPassengerRelatedWithSaleOrderDetailToPreview, PassengerDetailViewMode } from '../types';

export interface PassengerCommercialSummary {
	pendingPayAmount?: number;
	hasPendingPayAmount?: boolean;
	hasFutureTrips?: boolean;
}

export interface DetailField {
	label: string;
	value?: string | number | null;
}

export interface DetailSectionVisibility {
	showTravelDocuments: boolean;
	showFrequentFlyer: boolean;
	showSeatPreference: boolean;
	showBirthDate: boolean;
	showBillingDocument: boolean;
	showFiscalData: boolean;
	showCommercialSummary: boolean;
	showIdentityFields: boolean;
	filterInvoiceableSaleOrdersOnly: boolean;
}

const hasValue = (value?: string | number | null): value is string | number =>
	value !== undefined && value !== null && value !== '';

export const detailViewModeFromSearch = (search: string): PassengerDetailViewMode => {
	const from = new URLSearchParams(search).get('from');
	return from === 'customers' ? 'client' : 'passenger';
};

export const buildPassengerDetailUrl = (
	id: string,
	viewMode: PassengerDetailViewMode = 'passenger',
	agencyId?: number,
): string => {
	const base = routes.passengers.detail.replace(':id', id);
	const params = new URLSearchParams();
	if (viewMode === 'client') params.set('from', 'customers');
	// Contexto de BU: sin esto, abrir el link con otro ambiente activo daba 404
	if (agencyId) params.set('agencyId', String(agencyId));
	const query = params.toString();
	return query ? `${base}?${query}` : base;
};

export const getDetailBackRoute = (viewMode: PassengerDetailViewMode): string =>
	viewMode === 'client' ? routes.customers.list : routes.passengers.list;

export const getDetailPageTitle = (viewMode: PassengerDetailViewMode): string =>
	viewMode === 'client' ? 'Detalle de cliente' : 'Detalle de pasajero';

export const getDetailSectionVisibility = (
	detail: IPassengerDetail,
	viewMode: PassengerDetailViewMode,
): DetailSectionVisibility => {
	const isPhysicalPerson = detail.isPhysicalPerson;
	const isClientView = viewMode === 'client';

	return {
		showTravelDocuments: !isClientView && isPhysicalPerson,
		showFrequentFlyer: !isClientView && isPhysicalPerson,
		showSeatPreference: !isClientView && isPhysicalPerson,
		showBirthDate: isPhysicalPerson,
		showBillingDocument: true,
		showFiscalData: isClientView || !isPhysicalPerson,
		showCommercialSummary: isClientView,
		showIdentityFields: true,
		filterInvoiceableSaleOrdersOnly: isClientView,
	};
};

export const formatDetailDisplayName = (detail: IPassengerDetail): string => {
	if (detail.isPhysicalPerson) {
		return `${detail.name} ${detail.lastName}`.trim();
	}
	return detail.name?.trim() || detail.lastName?.trim() || 'Sin nombre';
};

export const formatDetailSubtitle = (detail: IPassengerDetail): string | undefined => {
	if (!detail.isPhysicalPerson && detail.lastName) {
		return detail.lastName;
	}
	return undefined;
};

export const formatOptionalDetailValue = (value?: string | number | null): string => {
	if (!hasValue(value)) return '—';
	return String(value);
};

export const formatDetailDate = (value?: string): string => {
	if (!hasValue(value)) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString('es-UY');
};

export const formatDetailDateTime = (value?: string): string => {
	if (!hasValue(value)) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString('es-UY');
};

export const formatDetailAmount = (value?: number): string => {
	if (value === undefined || value === null) return '—';
	return value.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const buildBillingDocumentLabel = (detail: IPassengerDetail): string | undefined => {
	if (!hasValue(detail.documentTypeName) && !hasValue(detail.documentNumber)) return undefined;
	if (hasValue(detail.documentTypeName) && hasValue(detail.documentNumber)) {
		return `${detail.documentTypeName} ${detail.documentNumber}`;
	}
	return String(detail.documentTypeName ?? detail.documentNumber);
};

const isFutureDate = (value?: string): boolean => {
	if (!hasValue(value)) return false;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return false;
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return date > today;
};

export const computeCommercialSummaryFromSaleOrders = (
	saleOrders: IPassengerRelatedWithSaleOrderDetailToPreview[],
	onlyInvoiceable = true,
): PassengerCommercialSummary => {
	const relevant = onlyInvoiceable ? saleOrders.filter((order) => order.invoiceable) : saleOrders;
	const hasAnyPendingField = relevant.some((order) => order.pendingPayAmount !== undefined);
	const hasAnyTravelDate = relevant.some((order) => order.travelDepartureDate !== undefined);

	if (!hasAnyPendingField && !hasAnyTravelDate) {
		return {};
	}

	const pendingPayAmount = relevant.reduce((sum, order) => sum + (order.pendingPayAmount ?? 0), 0);

	return {
		pendingPayAmount: hasAnyPendingField ? pendingPayAmount : undefined,
		hasPendingPayAmount: hasAnyPendingField ? pendingPayAmount > 0 : undefined,
		hasFutureTrips: hasAnyTravelDate ? relevant.some((order) => isFutureDate(order.travelDepartureDate)) : undefined,
	};
};

export type PendingBalanceChipColor = 'success' | 'warning' | 'error' | 'default';

/** Rojo: saldo > 0 sin viajes futuros · Amarillo: saldo > 0 con viajes futuros · Verde: saldo 0 */
export const resolvePendingBalanceChipColor = (summary: PassengerCommercialSummary): PendingBalanceChipColor => {
	if (summary.pendingPayAmount === undefined) return 'default';
	if (summary.pendingPayAmount <= 0) return 'success';
	if (summary.hasFutureTrips) return 'warning';
	return 'error';
};

export const resolvePassengerCommercialSummary = (
	detail: IPassengerDetail,
	onlyInvoiceable = true,
): PassengerCommercialSummary => {
	const fromApi: PassengerCommercialSummary = {
		pendingPayAmount: detail.pendingPayAmount,
		hasPendingPayAmount:
			detail.hasPendingPayAmount ?? (detail.pendingPayAmount !== undefined ? detail.pendingPayAmount > 0 : undefined),
		hasFutureTrips: detail.hasFutureTrips,
	};

	const fromSaleOrders = computeCommercialSummaryFromSaleOrders(detail.saleOrdersRelated ?? [], onlyInvoiceable);

	const pendingPayAmount = fromApi.pendingPayAmount ?? fromSaleOrders.pendingPayAmount;
	const hasPendingPayAmount =
		fromApi.hasPendingPayAmount ??
		fromSaleOrders.hasPendingPayAmount ??
		(pendingPayAmount !== undefined ? pendingPayAmount > 0 : undefined);

	return {
		pendingPayAmount,
		hasPendingPayAmount,
		hasFutureTrips: fromApi.hasFutureTrips ?? fromSaleOrders.hasFutureTrips,
	};
};

export const filterDetailFields = (fields: DetailField[]): DetailField[] =>
	fields.filter((field) => hasValue(field.value));

export type SaleOrderBalanceFilter = 'all' | 'withBalance' | 'withoutBalance';
export type SaleOrderDepartureSort = 'desc' | 'asc';

export const mapSaleOrderBalanceFilterToHasPending = (balanceFilter: SaleOrderBalanceFilter): boolean | undefined => {
	if (balanceFilter === 'withBalance') return true;
	if (balanceFilter === 'withoutBalance') return false;
	return undefined;
};

const parseDepartureSortKey = (value?: string): number => {
	if (!hasValue(value)) return 0;
	const time = new Date(value).getTime();
	return Number.isNaN(time) ? 0 : time;
};

export const filterSaleOrdersByBalance = (
	orders: IPassengerRelatedWithSaleOrderDetailToPreview[],
	balanceFilter: SaleOrderBalanceFilter,
): IPassengerRelatedWithSaleOrderDetailToPreview[] => {
	if (balanceFilter === 'all') return orders;
	if (balanceFilter === 'withBalance') {
		return orders.filter((order) => (order.pendingPayAmount ?? 0) > 0);
	}
	return orders.filter((order) => (order.pendingPayAmount ?? 0) <= 0);
};

export const sortSaleOrdersByDepartureDate = (
	orders: IPassengerRelatedWithSaleOrderDetailToPreview[],
	sort: SaleOrderDepartureSort,
): IPassengerRelatedWithSaleOrderDetailToPreview[] =>
	[...orders].sort((a, b) => {
		const diff = parseDepartureSortKey(b.travelDepartureDate) - parseDepartureSortKey(a.travelDepartureDate);
		return sort === 'desc' ? diff : -diff;
	});
